import type { SetStateAction } from 'react';
import { create } from 'zustand';
import { OEMType as EOEMType } from '@luna-protocol/core/src/types.ts';
import { PricingStatus } from './queries/useGetPricingStatus';
import { FlowTemplate, UserDetails } from './types';

type ResolvableAction<T> = SetStateAction<T>;

const isBrowser = typeof window !== 'undefined';

const getParsedSessionValue = <T>(value: string | null): T | undefined => {
  if (value === null) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const getSessionValue = <T>(key: string): T | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  const storedValue = window.sessionStorage.getItem(key);

  return getParsedSessionValue<T>(storedValue);
};

const setSessionValue = <T>(key: string, value: T | undefined) => {
  if (!isBrowser) {
    return;
  }

  if (value === undefined) {
    window.sessionStorage.removeItem(key);
    return;
  }

  window.sessionStorage.setItem(key, JSON.stringify(value));
};

const getLocalValue = <T>(key: string): T | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  const storedValue = window.localStorage.getItem(key);

  return getParsedSessionValue<T>(storedValue);
};

const setLocalValue = <T>(key: string, value: T | undefined) => {
  if (!isBrowser) {
    return;
  }

  if (value === undefined) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
};

const resolveStateAction = <T>(action: ResolvableAction<T>, current: T): T => {
  if (typeof action === 'function') {
    return (action as (prev: T) => T)(current);
  }

  return action;
};

export type AppStoreState = {
  loanApplicationID: string | undefined;
  isDealerDevice: boolean;
  isJoint: boolean;
  pricingStatus: PricingStatus | undefined;
  userDetails: UserDetails | undefined;
  dealerID: string;
  customerID: string | undefined;
  coapplicantID: string | undefined;
  OEMType: EOEMType;
  dealerFlowTemplate: FlowTemplate | undefined;
  setLoanApplicationID: (value: ResolvableAction<string | undefined>) => void;
  setIsDealerDevice: (value: ResolvableAction<boolean>) => void;
  setIsJoint: (value: ResolvableAction<boolean>) => void;
  setPricingStatus: (value: ResolvableAction<PricingStatus | undefined>) => void;
  setUserDetails: (value: ResolvableAction<UserDetails | undefined>) => void;
  setDealerID: (value: ResolvableAction<string>) => void;
  setCustomerID: (value: ResolvableAction<string | undefined>) => void;
  setCoapplicantID: (value: ResolvableAction<string | undefined>) => void;
  setOEMType: (value: ResolvableAction<EOEMType>) => void;
  setDealerFlowTemplate: (value: ResolvableAction<FlowTemplate | undefined>) => void;
};

export const useAppStore = create<AppStoreState>()(set => ({
  loanApplicationID: getSessionValue<string>('loanApplicationID'),
  isDealerDevice: getSessionValue<boolean>('isDealerDevice') ?? false,
  isJoint: getSessionValue<boolean>('isJoint') ?? false,
  pricingStatus: getSessionValue<PricingStatus>('pricingStatus'),
  userDetails: getSessionValue<UserDetails>('userDetails'),
  dealerID: getLocalValue<string>('dealer_id') ?? '',
  customerID: getSessionValue<string>('customerID'),
  coapplicantID: getSessionValue<string>('coapplicantID'),
  OEMType: getSessionValue<EOEMType>('OEMType') ?? EOEMType.Brunswick,
  dealerFlowTemplate: getLocalValue<FlowTemplate>('flow_template'),
  setLoanApplicationID: action =>
    set(state => {
      const nextLoanApplicationID = resolveStateAction(action, state.loanApplicationID);
      setSessionValue('loanApplicationID', nextLoanApplicationID);

      return { loanApplicationID: nextLoanApplicationID };
    }),
  setIsDealerDevice: action =>
    set(state => {
      const nextIsDealerDevice = resolveStateAction(action, state.isDealerDevice);
      setSessionValue('isDealerDevice', nextIsDealerDevice);

      return { isDealerDevice: nextIsDealerDevice };
    }),
  setIsJoint: action =>
    set(state => {
      const nextIsJoint = resolveStateAction(action, state.isJoint);
      setSessionValue('isJoint', nextIsJoint);

      return { isJoint: nextIsJoint };
    }),
  setPricingStatus: action =>
    set(state => {
      const nextPricingStatus = resolveStateAction(action, state.pricingStatus);
      setSessionValue('pricingStatus', nextPricingStatus);

      return { pricingStatus: nextPricingStatus };
    }),
  setUserDetails: action =>
    set(state => {
      const nextUserDetails = resolveStateAction(action, state.userDetails);
      setSessionValue('userDetails', nextUserDetails);

      return { userDetails: nextUserDetails };
    }),
  setDealerID: action =>
    set(state => {
      const nextDealerID = resolveStateAction(action, state.dealerID);
      setLocalValue('dealer_id', nextDealerID);

      return { dealerID: nextDealerID };
    }),
  setCustomerID: action =>
    set(state => {
      const nextCustomerID = resolveStateAction(action, state.customerID);
      setSessionValue('customerID', nextCustomerID);

      return { customerID: nextCustomerID };
    }),
  setCoapplicantID: action =>
    set(state => {
      const nextCoapplicantID = resolveStateAction(action, state.coapplicantID);
      setSessionValue('coapplicantID', nextCoapplicantID);

      return { coapplicantID: nextCoapplicantID };
    }),
  setOEMType: action =>
    set(state => {
      const nextOEMType = resolveStateAction(action, state.OEMType);
      setSessionValue('OEMType', nextOEMType);

      return { OEMType: nextOEMType };
    }),
  setDealerFlowTemplate: action =>
    set(state => {
      const nextDealerFlowTemplate = resolveStateAction(action, state.dealerFlowTemplate);
      setLocalValue('flow_template', nextDealerFlowTemplate);

      return { dealerFlowTemplate: nextDealerFlowTemplate };
    }),
}));
